// Reglas del catálogo: nombres, códigos, variantes y búsqueda.

export function validateProductName(value: string): string | null {
  const v = value.trim();
  if (v.length < 2) return 'El nombre debe tener al menos 2 caracteres.';
  if (v.length > 120) return 'El nombre puede tener hasta 120 caracteres.';
  return null;
}

export function validateCategoryName(value: string): string | null {
  const v = value.trim();
  if (v.length < 2) return 'El nombre debe tener al menos 2 caracteres.';
  if (v.length > 60) return 'El nombre puede tener hasta 60 caracteres.';
  return null;
}

/** Código de barras: dígitos o letras sin espacios (Code128 admite letras). */
export function normalizeCode(value: string): string {
  return value.trim().replace(/\s+/g, '').toUpperCase();
}

export function validateBarcode(value: string): string | null {
  const v = normalizeCode(value);
  if (v === '') return null;
  if (!/^[0-9A-Z.\-/]{3,32}$/.test(v)) return 'El código de barras tiene caracteres inválidos.';
  if (/^\d+$/.test(v) && [8, 12, 13].includes(v.length) && !hasValidEanCheckDigit(v)) {
    return 'El código no es un EAN/UPC válido (el último dígito no coincide). Revisá que esté bien escrito.';
  }
  return null;
}

/** Dígito verificador de EAN-8, UPC-A (12) y EAN-13. */
export function hasValidEanCheckDigit(code: string): boolean {
  const digits = [...code].map(Number);
  const check = digits.pop()!;
  const sum = digits
    .reverse()
    .reduce((acc, d, i) => acc + d * (i % 2 === 0 ? 3 : 1), 0);
  return (10 - (sum % 10)) % 10 === check;
}

export function validateSku(value: string): string | null {
  const v = value.trim();
  if (v === '') return null;
  if (v.length > 40) return 'El código interno puede tener hasta 40 caracteres.';
  return null;
}

/** Texto normalizado para buscar: minúsculas y sin acentos. */
export function normalizeSearch(value: string): string {
  return value
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .trim();
}

/**
 * Formas aceptadas de una palabra buscada: la escrita y, si parece plural, su singular, para que
 * "valvulas" encuentre "Válvula ..." y "motores" encuentre "Motor ...". Como el singular es el
 * comienzo del plural, buscar el singular también encuentra el plural.
 */
export function searchTermForms(term: string): string[] {
  const forms = [term];
  if (term.length > 4 && term.endsWith('ces')) forms.push(`${term.slice(0, -3)}z`); // luces → luz
  if (term.length > 4 && term.endsWith('es')) forms.push(term.slice(0, -2)); // motores → motor
  if (term.length > 3 && term.endsWith('s')) forms.push(term.slice(0, -1)); // valvulas → valvula
  return forms;
}

/** Palabras buscadas (normalizadas), cada una con sus formas aceptadas. */
export function searchTerms(query: string): string[][] {
  return normalizeSearch(query).split(/\s+/).filter(Boolean).map(searchTermForms);
}

/** ¿El texto (ya normalizado) tiene todas las palabras buscadas, en cualquier orden? */
export function matchesSearchTerms(text: string, terms: string[][]): boolean {
  return terms.every((forms) => forms.some((f) => text.includes(f)));
}

/** ¿Coinciden todas las palabras buscadas? ("coca 500" encuentra "Coca-Cola 500 ml"). */
export function matchesSearch(haystack: string, query: string): boolean {
  const terms = searchTerms(query);
  if (terms.length === 0) return true;
  return matchesSearchTerms(normalizeSearch(haystack), terms);
}

/**
 * Orden de un resultado según su nombre (menor = más arriba): 0 si el nombre empieza con la
 * primera palabra buscada, 1 si alguna palabra del nombre empieza con alguna buscada, 2 el resto
 * (la coincidencia está en medio de una palabra, en el código o en la categoría).
 */
export function searchRank(name: string, terms: string[][]): number {
  if (terms.length === 0) return 0;
  const words = normalizeSearch(name).split(/[^a-z0-9]+/).filter(Boolean);
  if (words.length > 0 && terms[0]!.some((f) => words[0]!.startsWith(f))) return 0;
  if (terms.some((forms) => forms.some((f) => words.some((w) => w.startsWith(f))))) return 1;
  return 2;
}

// ---------------------------------------------------------------------------
// Variantes (talle, color, etc.)
// ---------------------------------------------------------------------------

export interface VariantAttribute {
  name: string;
  values: string[];
}

export type VariantAttributes = Record<string, string>;

/** Separa "S, M, L" en valores limpios y sin repetidos. */
export function parseAttributeValues(input: string): string[] {
  const seen = new Set<string>();
  const result: string[] = [];
  for (const raw of input.split(/[,;\n]/)) {
    const v = raw.trim();
    const key = normalizeSearch(v);
    if (v && !seen.has(key)) {
      seen.add(key);
      result.push(v);
    }
  }
  return result;
}

/** Todas las combinaciones: [Talle: S,M] × [Color: Negro,Blanco] → 4 variantes. */
export function variantCombinations(attributes: VariantAttribute[]): VariantAttributes[] {
  const usable = attributes.filter((a) => a.name.trim() && a.values.length > 0);
  if (usable.length === 0) return [];
  return usable.reduce<VariantAttributes[]>(
    (combos, attr) => combos.flatMap((combo) => attr.values.map((v) => ({ ...combo, [attr.name.trim()]: v }))),
    [{}],
  );
}

export function variantLabel(attributes: VariantAttributes | null | undefined): string {
  if (!attributes) return '';
  return Object.values(attributes).filter(Boolean).join(' / ');
}

export const MAX_VARIANTS = 200;
